import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchSegmentData,
  fetchSkuSizeReplacementPacksAPI,
  fetchStateWithPackSegmentData,
  SegmentResponse,
} from "./packSegmentsAPI";
import { RootState } from "../../state/store";
import { hideLoader } from "../ui/loaderSlice";

export interface SegmentState {
  pricingAndDistribution: Record<string, number[]>;
  replace: Record<string, number[]>;
  addition: Record<string, number[]>;
  statesWithAvailablePackSegments: any;
  isPackReplacable: boolean;
  replacablePackSizes: { new_sku_size: string[] };
  loading: boolean;
  error: string | null;
}

const initialState: SegmentState = {
  pricingAndDistribution: {},
  replace: {},
  addition: {},
  isPackReplacable: false,
  replacablePackSizes: { new_sku_size: [] },
  statesWithAvailablePackSegments: {},
  loading: false,
  error: null,
};

export const fetchSegment = createAsyncThunk<SegmentResponse, any>(
  "segment/fetchSegment",
  async (payload: any, { dispatch }) => {
    try {
      const response = await fetchSegmentData(payload);
      const transformedResponse: any = {
        pricing_and_distribution: response.pricing_and_distribution[0]
          ? Object.assign({}, response.pricing_and_distribution[0])
          : {},
        deletion: response.deletion[0]
          ? Object.assign({}, response.deletion[0])
          : {},
        addition: response.addition[0]
          ? Object.assign({}, response.addition[0])
          : {},
      };

      return transformedResponse;
    } catch (error) {
      dispatch(hideLoader());
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchStateWithPackSegment = createAsyncThunk<any, any>(
  "segment/fetchStateWithPackSegment",
  async (payload: any, { dispatch }) => {
    try {
      const response = await fetchStateWithPackSegmentData(payload);

      return response;
    } catch (error) {
      dispatch(hideLoader());
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchSkuSizeReplacementPacks = createAsyncThunk<any, any>(
  "segment/fetchSkuSizeReplacementPacks",
  async (payload: any, { dispatch }) => {
    try {
      const response = await fetchSkuSizeReplacementPacksAPI(payload);
      return response;
    } catch (error) {
      dispatch(hideLoader());
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

const segmentSlice = createSlice({
  name: "segment",
  initialState,
  reducers: {
    updateSegmentData: (
      state,
      action: PayloadAction<{
        pricingAndDistribution?: Record<string, number[]>;
        replace?: Record<string, number[]>;
        addition?: Record<string, number[]>;
      }>
    ) => {
      const { pricingAndDistribution, replace, addition } = action.payload;
      if (pricingAndDistribution) {
        state.pricingAndDistribution = pricingAndDistribution;
      }
      if (replace) {
        state.replace = replace;
      }
      if (addition) {
        state.addition = addition;
      }
    },
    resetStatesWithAvailablePackSegments: (state) => {
      state.statesWithAvailablePackSegments = {};
    },
    setIsPackReplacable: (state, action) => {
      state.isPackReplacable = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSegment.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.pricingAndDistribution = {};
      state.replace = {};
      state.addition = {};
    });

    builder.addCase(
      fetchSegment.fulfilled,
      (state, action: PayloadAction<SegmentResponse>) => {
        state.loading = false;
        state.pricingAndDistribution = action.payload.pricing_and_distribution;
        state.replace = action.payload.deletion;
        state.addition = action.payload.addition;
      }
    );

    builder.addCase(fetchSegment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch segment data";
    });

    builder.addCase(fetchStateWithPackSegment.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.statesWithAvailablePackSegments = {};
    });

    builder.addCase(
      fetchStateWithPackSegment.fulfilled,
      (state, action: PayloadAction<Record<string, number[]>>) => {
        state.loading = false;
        state.statesWithAvailablePackSegments = action.payload.data;
      }
    );

    builder.addCase(fetchStateWithPackSegment.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.error.message || "Failed to fetch available pack segments";
    });

    builder.addCase(fetchSkuSizeReplacementPacks.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.replacablePackSizes = { new_sku_size: [] };
      state.isPackReplacable = true;
    });

    builder.addCase(
      fetchSkuSizeReplacementPacks.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.replacablePackSizes = action.payload;
        state.isPackReplacable = true;
      }
    );

    builder.addCase(fetchSkuSizeReplacementPacks.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.error.message || "Failed to fetch replacements packs";
      state.isPackReplacable = true;
    });
  },
});

export const {
  updateSegmentData,
  resetStatesWithAvailablePackSegments,
  setIsPackReplacable,
} = segmentSlice.actions;

export const selectPricingAndDistribution = (state: RootState) =>
  state.segment.pricingAndDistribution;
export const selectReplace = (state: RootState) => state.segment.replace;
export const selectAddition = (state: RootState) => state.segment.addition;
export const selectReplacablePackSizes = (state: RootState) =>
  state.segment.replacablePackSizes;
export const selectSegmentLoading = (state: RootState) => state.segment.loading;
export const selectSegmentError = (state: RootState) => state.segment.error;

export default segmentSlice.reducer;
